import React from 'react';
import {
    Box, Typography, Paper, Grid, Divider, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Stack, Button, TextField, Autocomplete
} from '@mui/material';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { getAllItineraryCards } from '../../api/itineraryCardsAPI';
import { computeExtraMattress, deriveCarDetailsFromSelections, findMatchingItineraryCard } from './createInquiryCalculation';

const InquiryPreview = ({
    guestInfo,
    tripDetails,
    stayInfo,
    itinerary,
    hotelSelections,
    allHotels,
    selectedPackage,
    cost,
    detailedItinerary,
    onDetailedItineraryChange,
    dayWiseDetailedItinerary = [],
    carSelections = {},
    inclusions = [],
    exclusions = [],
    storedItineraries: propStoredItineraries = [],
    allPackages = [],
    quotationNo = null
}) => {
    const [isEditingItinerary, setIsEditingItinerary] = React.useState(false);
    const [tempDetailedItinerary, setTempDetailedItinerary] = React.useState(detailedItinerary || '');
    const [storedItineraries, setStoredItineraries] = React.useState(propStoredItineraries || []);

    // Fetch itinerary cards
    React.useEffect(() => {
        const fetchStore = async () => {
            try {
                const res = await getAllItineraryCards();
                if (res.success) setStoredItineraries(res.data);
            } catch (err) {
                console.error("Failed to load itinerary cards", err);
            }
        };
        fetchStore();
    }, []);

    // Synchronize temp state when prop changes (from external edits)
    React.useEffect(() => {
        setTempDetailedItinerary(detailedItinerary || '');
    }, [detailedItinerary]);

    // Colors from PDF export
    const DARK_GREEN = '#1b5e20';
    const DARK = '#1f2937';
    const MID = '#6b7280';
    const LIGHT_BG = '#f5f7fa';

    const SectionTitle = ({ title, onEdit, isEditing }) => (
        <Box sx={{ backgroundColor: DARK_GREEN, py: 0.8, px: 1.5, mb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle2" sx={{ color: '#fff', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                {title}
            </Typography>
            {onEdit && (
                <Stack direction="row" spacing={1}>
                    {isEditing ? (
                        <>
                            <Button
                                size="small"
                                color="success"
                                variant="contained"
                                onClick={() => {
                                    onDetailedItineraryChange(tempDetailedItinerary);
                                    setIsEditingItinerary(false);
                                }}
                                sx={{ py: 0, px: 1, minWidth: 0, height: '22px', fontSize: '10px' }}
                            >
                                Save
                            </Button>
                            <Button
                                size="small"
                                color="error"
                                variant="contained"
                                onClick={() => {
                                    setTempDetailedItinerary(detailedItinerary || '');
                                    setIsEditingItinerary(false);
                                }}
                                sx={{ py: 0, px: 1, minWidth: 0, height: '22px', fontSize: '10px' }}
                            >
                                Cancel
                            </Button>
                        </>
                    ) : (
                        <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            onClick={() => setIsEditingItinerary(true)}
                            sx={{ py: 0, px: 1, minWidth: 0, height: '22px', fontSize: '10px' }}
                        >
                            Edit
                        </Button>
                    )}
                </Stack>
            )}
        </Box>
    );

    const InfoRow = ({ label, value }) => (
        <Box sx={{ display: 'flex', mb: 0.8 }}>
            <Typography sx={{ color: MID, width: '150px', fontSize: '0.85rem' }}>{label}</Typography>
            <Typography sx={{ color: DARK, fontWeight: 700, fontSize: '0.9rem' }}>{value || '—'}</Typography>
        </Box>
    );

    const TwoColRow = ({ lblL, valL, lblR, valR }) => (
        <Grid container spacing={2} sx={{ mb: 0.8 }}>
            <Grid item xs={6}>
                <Box sx={{ display: 'flex' }}>
                    <Typography sx={{ color: MID, width: '120px', fontSize: '0.85rem' }}>{lblL}</Typography>
                    <Typography sx={{ color: DARK, fontWeight: 700, fontSize: '0.875rem' }}>{valL || '—'}</Typography>
                </Box>
            </Grid>
            {lblR && (
            <Grid item xs={6}>
                <Box sx={{ display: 'flex' }}>
                    <Typography sx={{ color: MID, width: '120px', fontSize: '0.85rem' }}>{lblR}</Typography>
                    <Typography sx={{ color: DARK, fontWeight: 700, fontSize: '0.875rem' }}>{valR || '—'}</Typography>
                </Box>
            </Grid>
            )}
        </Grid>
    );

    const totalDays = (parseInt(tripDetails.duration, 10) || 0) + 1;
    const dayCars = deriveCarDetailsFromSelections(carSelections, totalDays);
    const activeCars = (tripDetails.car_details || []).filter(c => Number(c.car_count) > 0);
    const chosenCars = dayCars.length ? dayCars : activeCars;
    const vehicleStr = chosenCars.length
        ? chosenCars.map(c => `${c.car_name} (${c.car_count})`).join(', ')
        : '—';

    // Extra mattress — same per-night derivation as the "Select Hotels Day-Wise" section
    const extraMattressInfo = computeExtraMattress(hotelSelections, tripDetails);
    const extraMattressStr = extraMattressInfo.uniform
        ? `${extraMattressInfo.max} per night`
        : `up to ${extraMattressInfo.max} per night (${extraMattressInfo.perNight
            .filter(n => n.extra > 0)
            .map(n => `Night ${n.night}: ${n.extra}`)
            .join(', ')})`;
    // Shown together with "No of Person"
    const noOfPersonStr = extraMattressInfo.hasAny
        ? `${tripDetails.pax || 0}  (extra mattress: ${extraMattressStr})`
        : tripDetails.pax;

    return (
        <Box sx={{ p: 0, backgroundColor: '#fff', maxWidth: '800px', margin: '0 auto', border: '1px solid #eee' }}>
            {/* PDF Header Simulation */}
            <Box sx={{ position: 'relative', height: '100px', overflow: 'hidden', borderBottom: '1px solid #ddd' }}>
                <Box sx={{
                    position: 'absolute',
                    top: '-40px',
                    left: '-40px',
                    width: '120px',
                    height: '120px',
                    backgroundColor: DARK_GREEN,
                    borderRadius: '50%'
                }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
                    <Box sx={{ pl: 7 }}>
                        {quotationNo ? (
                            <Chip
                                label={`Quote ID: ${quotationNo}`}
                                size="small"
                                sx={{ bgcolor: '#e8f5e9', color: DARK_GREEN, fontWeight: 700 }}
                            />
                        ) : (
                            <Chip
                                label="Quote ID: Assigned upon save"
                                size="small"
                                variant="outlined"
                                sx={{ color: MID, borderColor: '#ccc', fontSize: '0.75rem' }}
                            />
                        )}
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="h5" sx={{ fontWeight: 800, color: DARK_GREEN, letterSpacing: '1px' }}>EASOTRIP</Typography>
                        <Typography variant="caption" sx={{ color: MID }}>Premium Travel Experiences</Typography>
                    </Box>
                </Box>
            </Box>

            <Box sx={{ p: 4 }}>
                {/* Guest Information */}
                <SectionTitle title="Guest & Booking Information" />
                <Box sx={{ px: 1, mb: 3 }}>
                    {quotationNo && <InfoRow label="Quote ID" value={quotationNo} />}
                    <InfoRow label="Name" value={guestInfo.guest_name} />
                    <InfoRow label="Phone" value={`${guestInfo.country_code || '+91'} ${guestInfo.guest_phone}`} />
                    <InfoRow label="Email" value={guestInfo.guest_email} />
                </Box>

                {/* Trip Details */}
                <SectionTitle title="Trip Details" />
                <Box sx={{ px: 1, mb: 3 }}>
                    <TwoColRow
                        lblL="Travel Date"
                        valL={tripDetails.travel_date ? new Date(tripDetails.travel_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                        lblR="No of Person"
                        valR={noOfPersonStr}
                    />
                    <TwoColRow
                        lblL="Duration"
                        valL={tripDetails.duration ? `${tripDetails.duration} Nights / ${Number(tripDetails.duration) + 1} Days` : '—'}
                        lblR="Kids (5+)"
                        valR={tripDetails.kids_above_5 || 0}
                    />
                    <TwoColRow
                        lblL="Pickup"
                        valL={tripDetails.pickup_location}
                        lblR="Rooms"
                        valR={stayInfo.rooms}
                    />
                    <TwoColRow
                        lblL="Dropoff"
                        valL={tripDetails.dropoff_location}
                        lblR="Vehicles"
                        valR={vehicleStr}
                    />
                </Box>

                {/* Short Itinerary */}
                {itinerary && itinerary.length > 0 && (
                    <>
                        <SectionTitle title="Short Itinerary" />
                        <Box sx={{ mb: 3 }}>
                            {itinerary.map((item, idx) => {
                                const itemText = typeof item === 'string' ? item : (item?.tagValue || item?.tagName || '');
                                return (
                                    <Box
                                        key={idx}
                                        sx={{
                                            display: 'flex',
                                            p: 1.2,
                                            backgroundColor: idx % 2 === 0 ? LIGHT_BG : 'transparent',
                                            alignItems: 'flex-start'
                                        }}
                                    >
                                        <Typography sx={{ fontWeight: 700, color: DARK_GREEN, width: '70px', fontSize: '0.85rem' }}>
                                            Day {idx + 1}:
                                        </Typography>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography sx={{ color: DARK, fontSize: '0.85rem' }}>
                                                {itemText}
                                            </Typography>
                                            {carSelections[idx] && carSelections[idx].length > 0 && (
                                                <Typography variant="caption" sx={{ color: DARK_GREEN, fontWeight: 700, mt: 0.5, display: 'block' }}>
                                                    Vehicles: {carSelections[idx].map(c => `${c.car_name} (${c.car_count})`).join(', ')}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    </>
                )}

                {/* Detailed Itinerary (if any or if editing) */}
                <SectionTitle
                    title="Detailed Itinerary"
                    isEditing={isEditingItinerary}
                />
                <Box sx={{ px: 1, mb: 3 }}>
                    {isEditingItinerary ? (
                        <>
                            <Autocomplete
                                options={storedItineraries}
                                getOptionLabel={(option) => option.title}
                                onChange={(event, newValue) => {
                                    if (newValue) {
                                        let html = '';
                                        newValue.itinerary?.forEach(day => {
                                            const headerText = day.headerTag ? `${day.headerTag}: ${day.headerValue || ''}` : (day.headerValue || '');
                                            html += `<h3>${headerText}</h3>`;
                                            if (day.Distance || day.Duration || day.Altitude || day.Time) {
                                                html += `<p>`;
                                                if (day.Distance) html += `<strong>Distance:</strong> ${day.Distance} &nbsp;`;
                                                if (day.Duration) html += `<strong>Duration:</strong> ${day.Duration} &nbsp;`;
                                                if (day.Altitude) html += `<strong>Altitude:</strong> ${day.Altitude} &nbsp;`;
                                                if (day.Time) html += `<strong>Time:</strong> ${day.Time}`;
                                                html += `</p>`;
                                            }
                                            if (day.para && day.para.length > 0) {
                                                day.para.forEach(p => {
                                                    html += p;
                                                });
                                            }
                                        });
                                        // Keep previous text if desired, or replace. The requirement implies replacing/loading.
                                        setTempDetailedItinerary(html);
                                    }
                                }}
                                renderInput={(params) => <TextField {...params} label="Load from Itinerary Store" variant="outlined" size="small" sx={{ mb: 2, bgcolor: '#fff' }} />}
                            />
                            <Box sx={{ bgcolor: '#fff', '& .ql-container': { minHeight: '150px', fontSize: '0.85rem', resize: 'vertical', overflowY: 'auto' } }}>
                                <ReactQuill
                                    theme="snow"
                                    value={tempDetailedItinerary}
                                    onChange={setTempDetailedItinerary}
                                    placeholder="Type or paste the detailed itinerary here..."
                                />
                            </Box>
                        </>
                    ) : (
                        <Box
                            className="ql-snow"
                            sx={{
                                '& .ql-editor': {
                                    p: 0,
                                    fontSize: '0.85rem',
                                    color: DARK,
                                    whiteSpace: 'pre-wrap',
                                    fontFamily: 'inherit',
                                    '& p': { mt: 0, mb: 1 },
                                    '& ul, & ol': { mt: 0, mb: 1, pl: 0 }
                                }
                            }}
                        >
                            {/* Display day-wise detailed itinerary if available, otherwise show HTML string */}
                            {(Array.isArray(dayWiseDetailedItinerary) && dayWiseDetailedItinerary.length > 0) || (Array.isArray(itinerary) && itinerary.length > 0) ? (
                                <Box sx={{ p: 1 }}>
                                    {Array.from({ length: Math.max(dayWiseDetailedItinerary.length, itinerary?.length || 0) }, (_, idx) => {
                                        const dayDetail = dayWiseDetailedItinerary[idx];
                                        const dayTitle = dayDetail?.dayTitle || itinerary?.[idx] || `Day ${idx + 1}`;
                                        const cardMatch = findMatchingItineraryCard(dayTitle, storedItineraries, allPackages);
                                        const paragraphs = (dayDetail?.para && dayDetail.para.length > 0)
                                            ? dayDetail.para
                                            : (cardMatch?.description ? [cardMatch.description] : []);

                                        return (
                                            <Box key={idx} sx={{ mb: 3, pb: 2, borderBottom: '1px solid #eee' }}>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: DARK_GREEN, mb: 1 }}>
                                                    Day {idx + 1}: {dayTitle}
                                                </Typography>
                                                {paragraphs.length > 0 ? (
                                                    <Box>
                                                        {paragraphs.map((paragraph, pIdx) => (
                                                            <Box key={pIdx} sx={{ mb: 1 }} dangerouslySetInnerHTML={{ __html: paragraph }} />
                                                        ))}
                                                    </Box>
                                                ) : (
                                                    <Typography variant="body2" sx={{ color: '#94a3b8', fontStyle: 'italic' }}>
                                                        No detailed itinerary description yet.
                                                    </Typography>
                                                )}
                                            </Box>
                                        );
                                    })}
                                </Box>
                            ) : (
                                <div
                                    className="ql-editor"
                                    dangerouslySetInnerHTML={{ __html: detailedItinerary ? (typeof detailedItinerary === 'string' ? detailedItinerary : 'No detailed itinerary provided yet.') : 'No detailed itinerary provided yet.' }}
                                />
                            )}
                        </Box>
                    )}
                </Box>

                {/* Inclusions & Exclusions */}
                {(inclusions?.length > 0 || exclusions?.length > 0) && (
                    <Grid container spacing={3} sx={{ mb: 3 }}>
                        {inclusions?.length > 0 && (
                            <Grid item xs={12} md={6}>
                                <SectionTitle title="Inclusions" />
                                <Box sx={{ px: 1 }}>
                                    {inclusions.map((inc, idx) => (
                                        <Typography key={`inc-${idx}`} sx={{ fontSize: '0.85rem', color: DARK, mb: 0.5 }}>
                                            • {inc}
                                        </Typography>
                                    ))}
                                </Box>
                            </Grid>
                        )}
                        {exclusions?.length > 0 && (
                            <Grid item xs={12} md={6}>
                                <SectionTitle title="Exclusions" />
                                <Box sx={{ px: 1 }}>
                                    {exclusions.map((exc, idx) => (
                                        <Typography key={`exc-${idx}`} sx={{ fontSize: '0.85rem', color: DARK, mb: 0.5 }}>
                                            • {exc}
                                        </Typography>
                                    ))}
                                </Box>
                            </Grid>
                        )}
                    </Grid>
                )}

                {/* Optional Extras */}
                {tripDetails.optional_extras && tripDetails.optional_extras.length > 0 && (
                    <Box sx={{ p: 3, border: '1px solid #eee', borderRadius: 2, mb: 3 }}>
                        <SectionTitle title="Optional Extras" />
                        <Box sx={{ mt: 2 }}>
                            {tripDetails.optional_extras.map((extra, idx) => (
                                <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '1px dashed #eee' }}>
                                    <Typography variant="body1" sx={{ fontSize: '0.85rem' }}>
                                        • {extra.name} {extra.count > 1 ? `(x${extra.count})` : ''}
                                    </Typography>
                                    <Typography variant="body1" fontWeight={600} sx={{ fontSize: '0.85rem' }}>
                                        {extra.include_in_cost !== false ? (
                                            <Typography component="span" variant="caption" sx={{ color: DARK_GREEN, fontWeight: 700, fontStyle: 'italic' }}>
                                                Included in Package
                                            </Typography>
                                        ) : (
                                            <>
                                                ₹{((extra.price || 0) * (extra.count || 1)).toLocaleString()}
                                                <Typography component="span" variant="caption" color="error" sx={{ ml: 1, fontWeight: 'normal' }}>
                                                    (Payable locally - Not in package cost)
                                                </Typography>
                                            </>
                                        )}
                                    </Typography>
                                </Box>
                            ))}
                            {/* The Total Extras Cost row was removed as per user request (it is either Included or Payable Locally) */}
                        </Box>
                    </Box>
                )}

                {/* Hotel Selections */}
                {(() => {
                    const nightCount = parseInt(tripDetails.duration) || 0;
                    const validSelections = [];
                    Object.keys(hotelSelections).sort((a, b) => Number(a) - Number(b)).forEach(d => {
                        if (validSelections.length >= nightCount) return; // cap to actual nights
                        const arr = Array.isArray(hotelSelections[d]) ? hotelSelections[d] : (hotelSelections[d] ? [hotelSelections[d]] : []);
                        arr.forEach(sel => {
                            if (sel && sel.hotelId && validSelections.length < nightCount) validSelections.push({ dayIdx: d, ...sel });
                        });
                    });

                    if (validSelections.length === 0) return null;

                    return (
                        <>
                            <SectionTitle title="Hotel Selections" />
                            <TableContainer sx={{ mb: 3, border: '1px solid #eee' }}>
                                <Table size="small">
                                    <TableHead sx={{ backgroundColor: '#eeeeee' }}>
                                        <TableRow>
                                            <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700 }}>Night</TableCell>
                                            <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700 }}>Hotel</TableCell>
                                            <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700 }}>Room Type</TableCell>
                                            <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700 }}>Meal Plan</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {validSelections.map((sel, i) => {
                                            const hotel = allHotels.find(h => h._id?.toString() === sel.hotelId?.toString());
                                            const actualRooms = sel.roomsCount || stayInfo.rooms || 1;
                                            const mealPlanLabel = {
                                                cp_plan: 'Breakfast',
                                                map_plan: 'Breakfast + 1 major meal',
                                                ap_plan: 'All meals',
                                            }[sel.mealPlan] || sel.mealPlan || '—';
                                            
                                            const roomTypeStr = sel.roomType ? (sel.roomType + (actualRooms > 1 ? ` (x${actualRooms})` : '')) : '—';

                                            return (
                                                <TableRow key={`${sel.dayIdx}-${i}`} sx={{ backgroundColor: i % 2 === 0 ? LIGHT_BG : 'transparent' }}>
                                                    <TableCell sx={{ fontSize: '0.75rem' }}>Night {i + 1}</TableCell>
                                                    <TableCell sx={{ fontSize: '0.75rem' }}>{hotel?.hotel_name || '—'}</TableCell>
                                                    <TableCell sx={{ fontSize: '0.75rem' }}>{roomTypeStr}</TableCell>
                                                    <TableCell sx={{ fontSize: '0.75rem' }}>{mealPlanLabel}</TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </>
                    );
                })()}

                {/* Important Notes */}
                {tripDetails?.important_notes && tripDetails.important_notes.trim() !== '' && (
                    <>
                        <SectionTitle title="Important Notes" />
                        <Box sx={{ mb: 3, px: 1 }}>
                            {tripDetails.important_notes.split('\n').map((line, idx) => (
                                <Typography key={idx} sx={{ color: DARK, fontSize: '0.85rem', mb: 0.5 }}>
                                    {line}
                                </Typography>
                            ))}
                        </Box>
                    </>
                )}

                {/* Total Cost Bar */}
                <Box sx={{
                    backgroundColor: DARK_GREEN,
                    p: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 3
                }}>
                    <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.1rem' }}>TOTAL QUOTED PRICE</Typography>
                    <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '1.2rem' }}>INR {cost.toLocaleString('en-IN')}</Typography>
                </Box>

                {/* Terms & Footer */}
                <Box sx={{ borderTop: '1px solid #eee', pt: 2 }}>
                    <Typography variant="caption" sx={{ color: MID, fontWeight: 700, display: 'block', mb: 1 }}>Terms & Conditions</Typography>
                    <Typography variant="caption" sx={{ color: MID, display: 'block' }}>• This is a preliminary quotation and is subject to change based on availability.</Typography>
                    <Typography variant="caption" sx={{ color: MID, display: 'block' }}>• Rates are valid for the indicated travel dates only.</Typography>
                    <Typography variant="caption" sx={{ color: MID, display: 'block' }}>• 25% advance required to confirm bookings.</Typography>
                    <Typography variant="caption" sx={{ color: MID, display: 'block' }}>• Cancellation charges apply as per policy.</Typography>
                </Box>
            </Box>

            {/* Footer Bar */}
            <Box sx={{ backgroundColor: DARK_GREEN, p: 1.5, display: 'flex', justifyContent: 'center', gap: 4 }}>
                <Typography variant="caption" sx={{ color: '#fff' }}>www.easotrip.com</Typography>
                <Typography variant="caption" sx={{ color: '#fff' }}>+91 70017 24300</Typography>
                <Typography variant="caption" sx={{ color: '#fff' }}>easogroup@gmail.com</Typography>
            </Box>
        </Box>
    );
};

export default InquiryPreview;
